import { useMemo, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { Spinner } from '@/components/ui/spinner'
import { useMediaQuery } from '@/lib/use-media-query'
import { cn } from '@/lib/utils'

export type SortDir = 'asc' | 'desc'

export interface DataTableColumn<T> {
  /** Stable identity — sort state keys off it. */
  id: string
  header: string
  cell: (row: T) => ReactNode
  /** `right` = numeric column: right-aligned mono 13px. */
  align?: 'left' | 'right'
  /**
   * Width utility applied to the `<th>` (e.g. `w-28` for a date column).
   * Alongside `flex`, use it for a `min-w-*` floor so the slack column never
   * collapses to nothing at narrow widths.
   */
  widthClassName?: string
  /** Fixed-content column (date/money/chip/count): keeps the cell on one line. */
  nowrap?: boolean
  /**
   * The ONE slack column per table. Takes all leftover width (`w-full`) with
   * `max-w-0` on both `<th>` and `<td>` so long values truncate instead of
   * stretching the table. Cell content is wrapped in a `truncate` block —
   * string/number content gets an automatic `title` with the full value;
   * element content (two-line stacks) should truncate + title its own lines.
   */
  flex?: boolean
  /** Present ⇒ the header sorts client-side on this value. */
  sortAccessor?: (row: T) => string | number
  /** Direction the FIRST click applies (amounts/counts read best `desc`). */
  defaultSortDir?: SortDir
}

export interface DataTableSort {
  columnId: string
  dir: SortDir
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string | undefined
  /** Starting sort; with none, rows render in the order given (server order). */
  initialSort?: DataTableSort
  /**
   * Pins header cells with `sticky top-0`. CAVEAT: the component always wraps
   * the table in its own `overflow-x-auto`, so sticky engages against that
   * wrapper — it only reads as pinned when the PAGE owns vertical scroll and
   * the table isn't overflowing horizontally. No scroll-context detection is
   * attempted here.
   */
  stickyHeader?: boolean
  loading?: boolean
  loadingLabel?: string
  /** Failure slot (typically an ErrorState) — rendered instead of the table. */
  error?: ReactNode
  /** Zero-rows slot (typically an EmptyState). */
  empty?: ReactNode
  /** Below 768px the table becomes a stacked card list of these. */
  renderCard?: (row: T) => ReactNode
  /** Screen-reader table caption. */
  caption?: string
}

/**
 * The Ledger data table: real `<table>` semantics (`<th scope="col">`,
 * `aria-sort`), 40px-density rows, mono numeric columns, client sort with the
 * asc/desc cycle the orders list established, and loading/error/empty slots so
 * pages stop hand-rolling those states.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  initialSort,
  stickyHeader = false,
  loading = false,
  loadingLabel,
  error,
  empty,
  renderCard,
  caption,
}: DataTableProps<T>) {
  // null = caller's row order; clicking a sortable header takes over.
  const [sort, setSort] = useState<DataTableSort | null>(initialSort ?? null)
  const narrow = useMediaQuery('(max-width: 767px)')

  const sortAccessor = sort
    ? columns.find((column) => column.id === sort.columnId)?.sortAccessor
    : undefined
  const sorted = useMemo(() => {
    if (!sort || !sortAccessor) return rows
    const factor = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = sortAccessor(a)
      const bv = sortAccessor(b)
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor
      return String(av).localeCompare(String(bv)) * factor
    })
  }, [rows, sort, sortAccessor])

  function toggleSort(column: DataTableColumn<T>) {
    setSort((current) =>
      current?.columnId === column.id
        ? { columnId: column.id, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { columnId: column.id, dir: column.defaultSortDir ?? 'asc' },
    )
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner label={loadingLabel} />
      </div>
    )
  }

  if (error) return <>{error}</>

  if (rows.length === 0 && empty) return <>{empty}</>

  if (narrow && renderCard) {
    return (
      <ul className="space-y-2">
        {sorted.map((row) => (
          <li key={rowKey(row)}>{renderCard(row)}</li>
        ))}
      </ul>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr className="border-b border-rule/70">
            {columns.map((column) => {
              const activeSort =
                column.sortAccessor && sort?.columnId === column.id ? sort : null
              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={
                    activeSort ? (activeSort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  className={cn(
                    // Headers never wrap — a width utility tighter than the
                    // tracked mono label must widen the column, not fold it.
                    'whitespace-nowrap px-3 py-2.5 first:pl-2 last:pr-2',
                    column.align === 'right' && 'text-right',
                    column.flex && 'w-full max-w-0',
                    column.widthClassName,
                    stickyHeader && 'sticky top-0 z-(--z-sticky) bg-card',
                  )}
                >
                  {column.sortAccessor ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column)}
                      className="micro-label inline-flex items-center gap-1 rounded-sm transition-colors duration-[180ms] ease-editorial hover:text-ink"
                    >
                      {column.header}
                      {activeSort ? (
                        activeSort.dir === 'asc' ? (
                          <ArrowUp aria-hidden="true" className="size-3" />
                        ) : (
                          <ArrowDown aria-hidden="true" className="size-3" />
                        )
                      ) : null}
                    </button>
                  ) : (
                    <span className="micro-label">{column.header}</span>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                'border-b border-rule/40 transition-colors duration-[180ms] ease-editorial',
                onRowClick && 'cursor-pointer hover:bg-cream',
                rowClassName?.(row),
              )}
            >
              {columns.map((column) => {
                const content = column.cell(row)
                return (
                  <td
                    key={column.id}
                    className={cn(
                      'px-3 py-2.5 first:pl-2 last:pr-2',
                      column.align === 'right' && 'text-right font-mono text-[0.8125rem]',
                      column.nowrap && 'whitespace-nowrap',
                      column.flex && 'w-full max-w-0',
                    )}
                  >
                    {column.flex ? (
                      <div
                        className="truncate"
                        title={
                          typeof content === 'string' || typeof content === 'number'
                            ? String(content)
                            : undefined
                        }
                      >
                        {content}
                      </div>
                    ) : (
                      content
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
